import { translate } from '../i18n';
import {
    CapacityPricingWarningsDto,
    SportCapacityPricingWarningDto,
} from '../models/court/dto/capacity-pricing-warning.dto';

/**
 * What a COURT write took away, in the operator's words (D136).
 *
 * The court-side mirror of `game-capacity-management/utils/capacity-write-warnings.ts`,
 * which reports the same loss from the capacity-OPTION side. Priceability is a
 * property of two independent sets — the sizes an organization offers, and the
 * pricing rows that give them a price — and the court rails move the second
 * one: narrowing a court's sports, archiving a court, or deleting an
 * availability can take away the venue's LAST price for a size the capacity tab
 * still offers and the admin booking modal still lets an operator pick. The
 * booking is then accepted and 404s when it is priced.
 *
 * Lives in `src/utils` rather than in one of the two features because three
 * screens across `court-details` and `court-management` show it, and a
 * per-screen copy is exactly how the option-side wording drifted before it was
 * shared.
 *
 * Deliberately NOT merged with the option-side helper: that one reports three
 * different fields about a single sport, this one reports one field per sport,
 * and a shared abstraction would have to carry both shapes. What IS shared is
 * the rule — warn, never block, and join every sentence into ONE toast so the
 * second is not buried under the first.
 */
export function buildCapacityPricingLossWarning(
    result: CapacityPricingWarningsDto | undefined | null
): string | null {
    const rows: SportCapacityPricingWarningDto[] =
        result?.capacityPricingWarnings ?? [];

    const sentences = rows
        // The API returns a row only for a sport that LOST something, so an
        // empty list here is an API that predates the field or a shape nobody
        // should render a toast for. Guarded rather than trusted, because
        // "{{sport}} is still offered for  players" is worse than silence.
        .filter(row => row.unpricedCapacities.length > 0)
        .map(row =>
            translate('courts.capacity_pricing_lost')
                .replace(/\{\{sport\}\}/g, row.sport)
                .replace(/\{\{capacities\}\}/g, row.unpricedCapacities.join(', '))
        );

    return sentences.length > 0 ? sentences.join(' ') : null;
}
